import { jsxs as _jsxs, jsx as _jsx } from "preact/jsx-runtime";
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'preact/hooks';
import { useAuth } from '@/auth/useAuth';
import { useEmailAssetChat } from '@/hooks/useEmailAssetChat';
import { UnlayerDesigner } from '@/components/assets/UnlayerDesigner';
import { EmailCopyEditorPanel } from '@/components/assets/EmailCopyEditorPanel';
import { RocketLaunch } from '@/components/webs/landingPage/RocketLaunch';
import { WebsterChatPanel } from '@/components/webster/WebsterChatPanel';
import { SaveStatusIndicator } from '@/components/common/SaveStatusIndicator';
import { assetApi } from '@/api/asset';
// Merge tag pills exposed in the Email Template editor's Unlayer UI. Template authors
// drop these into the design; the exported HTML preserves them as literal `{{NAME}}`
// strings. `{{BODY}}` / `{{CTA_TEXT}}` / `{{CTA_URL}}` are markers used by the
// template-only preview (PreviewEmailTemplate); copies clone the template's design and
// own the rendered content directly, so they expose a different set (see ORB-289).
// `{{first_name}}` passes through unsubstituted at render time and is filled by the
// future per-recipient send service. `{{event_title}}` and other web-entity variables
// resolve via the server-side variable contract at preview/send.
const TEMPLATE_MERGE_TAGS = {
    BODY: {
        name: 'Email Body',
        value: '{{BODY}}',
        sample: 'The email copy body will be inserted here at send time.',
    },
    CTA_TEXT: {
        name: 'CTA Button Text',
        value: '{{CTA_TEXT}}',
        sample: 'Save your spot',
    },
    CTA_URL: {
        name: 'CTA Destination URL',
        value: '{{CTA_URL}}',
        sample: 'https://example.com/register',
    },
    first_name: {
        name: 'Recipient First Name',
        value: '{{first_name}}',
        sample: 'Sarah',
    },
    event_title: {
        name: 'Event Title',
        value: '{{event_title}}',
        sample: 'Reclaiming Source-of-Funds Reviews',
    },
};
export function EmailBuilder({ clientCompanyId, workspaceId, campaignId, webId, assetId, assetType, assetDetail, refreshAsset, flushRef, reseedRef, onGeneratingChange, }) {
    const unlayerEditorRef = useRef(null);
    const emailCopyPanelRef = useRef(null);
    const draftHtml = assetDetail?.DraftHtml;
    const draftDesign = assetDetail?.DraftDesign;
    const draftConversationSessionId = assetDetail?.DraftConversationSessionId;
    // ── Template canvas auto-save ──
    // Persists BOTH Unlayer's design JSON (canonical editable source) and its exported
    // HTML (used for preview / send). Pattern mirrors EmailCopyEditorPanel: every change
    // marks dirty, a debounced timer fires the save, the last-persisted refs let the
    // post-save refetch land without re-mounting the editor or looping save requests.
    const [templateSaveStatus, setTemplateSaveStatus] = useState('idle');
    const [templateSaveError, setTemplateSaveError] = useState(null);
    const pendingHtmlRef = useRef(null);
    const pendingDesignRef = useRef(null);
    const lastPersistedHtmlRef = useRef(draftHtml ?? null);
    const lastPersistedDesignRef = useRef(assetDetail?.DraftDesign ?? null);
    const saveTimerRef = useRef(null);
    const runTemplateSave = useCallback(async () => {
        if (!assetDetail)
            return;
        const htmlSnapshot = pendingHtmlRef.current;
        const designSnapshot = pendingDesignRef.current;
        const htmlChanged = htmlSnapshot != null && htmlSnapshot !== lastPersistedHtmlRef.current;
        const designChanged = designSnapshot != null && designSnapshot !== lastPersistedDesignRef.current;
        if (!htmlChanged && !designChanged)
            return;
        setTemplateSaveStatus('saving');
        setTemplateSaveError(null);
        const response = await assetApi.updateAsset(clientCompanyId, workspaceId, campaignId, assetDetail.Id, {
            Id: assetDetail.Id,
            CampaignId: assetDetail.CampaignId,
            ...(htmlChanged && htmlSnapshot != null ? { DraftHtml: htmlSnapshot } : {}),
            ...(designChanged && designSnapshot != null ? { DraftDesign: designSnapshot } : {}),
        });
        if (response.data) {
            if (htmlSnapshot != null)
                lastPersistedHtmlRef.current = htmlSnapshot;
            if (designSnapshot != null)
                lastPersistedDesignRef.current = designSnapshot;
            // If a fresh edit landed while in-flight, stay dirty so the next debounce
            // tick triggers another save instead of falsely showing "Saved".
            const stillMatches = pendingHtmlRef.current === htmlSnapshot &&
                pendingDesignRef.current === designSnapshot;
            setTemplateSaveStatus(stillMatches ? 'saved' : 'dirty');
            await refreshAsset();
        }
        else {
            setTemplateSaveStatus('error');
            setTemplateSaveError(response.error ?? 'Save failed.');
        }
    }, [clientCompanyId, workspaceId, campaignId, assetDetail, refreshAsset]);
    // Unlayer fires this on every design:updated event with both the design JSON and the
    // freshly-compiled HTML. We capture both into pending refs and schedule a debounced
    // save — same cadence pattern Email Copy uses.
    const handleUnlayerUpdate = useCallback((design, html) => {
        pendingDesignRef.current = design;
        pendingHtmlRef.current = html;
        const designChanged = design !== lastPersistedDesignRef.current;
        const htmlChanged = html !== lastPersistedHtmlRef.current;
        if (!designChanged && !htmlChanged)
            return;
        setTemplateSaveStatus('dirty');
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => { void runTemplateSave(); }, 800);
    }, [runTemplateSave]);
    const handleTemplateSaveRetry = useCallback(() => { void runTemplateSave(); }, [runTemplateSave]);
    // Force Unlayer to export its current design + HTML and persist whatever differs from
    // the last save. Called by Save as Version before promote, so a freshly Webster-generated
    // template with null DraftHtml gets compiled (Unlayer's `design:updated` only fires after
    // an edit settles; users can otherwise click Save before that lands).
    const flushTemplate = useCallback(async () => {
        if (!unlayerEditorRef.current)
            return;
        const design = await unlayerEditorRef.current.getDesign();
        const html = await unlayerEditorRef.current.getHtml();
        pendingDesignRef.current = design;
        pendingHtmlRef.current = html;
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        await runTemplateSave();
    }, [runTemplateSave]);
    // Always register the flush callback for email assets. The flush snapshots Unlayer's
    // current design + HTML, cancels any pending debounce, and persists whatever differs
    // from the last save — covers the "freshly Webster-generated, null DraftHtml" case
    // AND the "user typed within the 800ms debounce window" case where the canvas is
    // dirtier than the last persisted state. Both flush implementations are no-ops when
    // nothing has changed, so there's no cost to always registering.
    useEffect(() => {
        flushRef.current = assetType === 'Email Template'
            ? flushTemplate
            : async () => { await emailCopyPanelRef.current?.flushAndSave(); };
        return () => { flushRef.current = null; };
    }, [assetType, flushTemplate, flushRef]);
    // Cleanup the pending debounce on unmount so a stale timer can't fire against a
    // disposed component.
    useEffect(() => () => {
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
    }, []);
    // Auto-dismiss the "Saved" pill ~1.5s after it settles so it doesn't permanently
    // overlap the eye/preview button at the top-right of the canvas. Any new edit flips
    // status back to 'dirty' before this fires; the cleanup function tears down the
    // timer in that case, so the pill seamlessly transitions to 'Saving…' instead of
    // briefly hiding.
    useEffect(() => {
        if (templateSaveStatus !== 'saved')
            return;
        const t = setTimeout(() => setTemplateSaveStatus('idle'), 1500);
        return () => clearTimeout(t);
    }, [templateSaveStatus]);
    // Shell state is now driven by the asset's persisted DraftGenerationStatus
    // instead of inferred from `!hasDraft`. The inference was wrong on the
    // failure path: a failed generation has no draft, so the old logic kept
    // the rocket animation running indefinitely on re-entry. The server now
    // persists Failed (with DraftGenerationError) on the SB trigger's catch
    // block, so we have a real signal here.
    //
    // Legacy assets (pre-ORB-294 schema) have DraftGenerationStatus=null;
    // fall back to the legacy hasDraft heuristic so they still work until a
    // fresh generation overwrites the status.
    const hasDraft = !!draftHtml || !!draftDesign;
    const persistedGenStatus = assetDetail?.DraftGenerationStatus ?? null;
    const isFailed = persistedGenStatus === 'Failed';
    const isPersistedGenerating = persistedGenStatus === 'Generating';
    const shouldStartGenerating = isPersistedGenerating || (persistedGenStatus === null && !hasDraft);
    const [autoGenerating, setAutoGenerating] = useState(shouldStartGenerating);
    const [rocketPhase, setRocketPhase] = useState(shouldStartGenerating ? 'idle' : 'ready');
    // Bumped to remount the template canvas off a freshly restored draft after a revert.
    // Auto-save deliberately does NOT bump this (same content-compare reasoning as
    // EmailCopyEditorPanel) — remounting mid-edit would drop the user's place.
    const [templateDesignerKey, setTemplateDesignerKey] = useState(0);
    // The rocket blast-off splash is for the INITIAL design only. Seeded from whether a
    // draft already exists at mount: a brand-new (or failed, draft-less) asset rockets on
    // its first successful generation; an asset opened with an existing design treats every
    // edit as a follow-up. Follow-up edits reseed the editor in place instead of replaying
    // the splash — it's too much now that edits are fast (ORB-314).
    const hasInitialDesignRef = useRef(hasDraft);
    // 'rebuilt' SignalR event no longer carries HTML — refetch the asset and pull the
    // canonical DraftDesign / DraftHtml from the response, then reseed the editor with the
    // fresh content. Reset our auto-save tracking refs for Email Template so the user's
    // first post-rebuild edit registers as dirty. Email Copy's panel manages its own design
    // save tracking internally and reseeds off the new asset prop, so no parent-side reset.
    //
    // The rocket plays ONLY for the initial design (hasInitialDesignRef); follow-up edits
    // reseed in place — template canvas key-bump; Email Copy self-reseeds off the prop.
    const handleDraftRebuilt = useCallback(async () => {
        const fresh = (await refreshAsset());
        if (assetType === 'Email Template' && fresh) {
            lastPersistedDesignRef.current = fresh.DraftDesign ?? null;
            lastPersistedHtmlRef.current = fresh.DraftHtml ?? null;
            pendingDesignRef.current = null;
            pendingHtmlRef.current = null;
            setTemplateSaveStatus('idle');
            if (saveTimerRef.current)
                clearTimeout(saveTimerRef.current);
        }
        if (!hasInitialDesignRef.current) {
            // Initial design: play the full rocket blast-off, then mark it shown.
            hasInitialDesignRef.current = true;
            setRocketPhase('blasting-off');
        }
        else if (assetType === 'Email Template') {
            // Follow-up edit: no rocket — remount the canvas off the fresh design so it
            // reloads in place. (Email Copy reseeds itself off the refreshed asset prop above.)
            setTemplateDesignerKey(k => k + 1);
        }
    }, [refreshAsset, assetType]);
    // Revert ("Revert to last version") reloads the editor off the restored draft. Unlike
    // a rebuild it's not animated — no rocket — but it shares the canonical-draft reseed:
    // refetch the asset, reset the auto-save tracking refs to the restored content so the
    // next edit registers as dirty, then remount the template canvas. The Email Copy panel
    // reseeds itself off the refreshed asset prop, so no key bump is needed there.
    const reseedAfterRevert = useCallback(async () => {
        const fresh = (await refreshAsset());
        // Email Copy reseeds itself off the refreshed asset prop; only the template canvas needs
        // an explicit remount + auto-save-ref reset off the restored draft. Early-return otherwise.
        if (assetType !== 'Email Template' || !fresh)
            return;
        lastPersistedDesignRef.current = fresh.DraftDesign ?? null;
        lastPersistedHtmlRef.current = fresh.DraftHtml ?? null;
        pendingDesignRef.current = null;
        pendingHtmlRef.current = null;
        setTemplateSaveStatus('idle');
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
        setTemplateDesignerKey(k => k + 1);
    }, [refreshAsset, assetType]);
    useEffect(() => {
        reseedRef.current = reseedAfterRevert;
        return () => { reseedRef.current = null; };
    }, [reseedAfterRevert, reseedRef]);
    const handleRocketComplete = useCallback(() => {
        setRocketPhase('ready');
        setAutoGenerating(false);
    }, []);
    // Local autoGenerating tracks the IN-MEMORY belief that generation is in
    // flight. When the SB worker writes Failed (persistedGenStatus updates on
    // the next assetDetail refresh) OR an error SignalR event fires, we need
    // to flip autoGenerating off so the failed UI surfaces immediately
    // (rocket animation stops, input unlocks, error display renders). Without
    // this effect, autoGenerating would stay true from its initial-mount
    // value and the rocket would keep animating over the failure banner.
    useEffect(() => {
        if (isFailed && autoGenerating) {
            setAutoGenerating(false);
            setRocketPhase('ready');
        }
    }, [isFailed, autoGenerating]);
    const { userProfile } = useAuth();
    const { messages, generating, sendMessage, beginAutoGeneration, turnState, turnUserId, turnUserName, } = useEmailAssetChat({
        clientCompanyId,
        workspaceId,
        campaignId,
        assetId,
        assetType,
        initialSessionId: draftConversationSessionId,
        onDraftRebuilt: handleDraftRebuilt,
        // Refetch on a SignalR error so DraftGenerationStatus picks up the
        // Failed value the SB worker just wrote — the useEffect above flips
        // autoGenerating off as soon as isFailed turns true, surfacing the
        // failed UI without making the user refresh.
        onGenerationError: refreshAsset,
    });
    const isGenerating = generating || autoGenerating;
    // Open the chat working indicator at generation START for the server-initiated initial
    // generation, so the chat's elapsed clock is continuous (not reset when the first SignalR
    // event lands seconds later) and the chat's `generating` reflects the real lifecycle —
    // ending at `rebuilt`, independent of the rocket animation (autoGenerating, which lingers
    // through the blast-off). useLayoutEffect + a once-guard so it anchors before first paint
    // and fires a single time even as shouldStartGenerating recomputes across status refreshes.
    const autoGenStartedRef = useRef(false);
    useLayoutEffect(() => {
        if (shouldStartGenerating && !autoGenStartedRef.current) {
            autoGenStartedRef.current = true;
            beginAutoGeneration();
        }
    }, [shouldStartGenerating, beginAutoGeneration]);
    // Multi-user turn lock. When another user is mid-turn on the shared chat
    // session, lock this user's input and attribute the wait. Null when idle
    // or when this user holds the turn (input is already disabled by isGenerating).
    // Fallback to a generic label if turnUserName is missing — without it the
    // input wouldn't lock at all (lockedByUserName goes null), defeating the
    // turn-lock UX when the holder's name happens to be empty on the server side.
    const lockedByUserName = (turnState === 'Active' && turnUserId && turnUserId !== userProfile?.Id)
        ? (turnUserName || 'Another user')
        : null;
    // Bubble the full generating state up so the parent overlay can disable Save-as-Version
    // while Webster is working — initial auto-generation (autoGenerating) AND chat-driven
    // rebuilds (generating). Reporting only `generating` here let users save an un-generated
    // draft during the initial generation (ORB-314).
    useEffect(() => {
        onGeneratingChange?.(isGenerating);
    }, [isGenerating, onGeneratingChange]);
    const placeholder = assetType === 'Email Template'
        ? 'Describe the email template you want...'
        : 'Describe the email copy you want...';
    const emptyMessage = assetType === 'Email Template'
        ? 'Ask Webster to create or modify your email template. Describe the design, layout, or changes you want.'
        : 'Ask Webster to draft or refine your email copy. Describe the message, tone, or changes you want.';
    return (_jsxs("div", { className: "h-full flex overflow-hidden", children: [_jsx("div", { className: "flex-1 min-w-0 overflow-hidden", children: isFailed ? (
                /* Persisted Failed status from the SB worker's catch block.
                   Surfaces the actual error and a Try Again affordance instead
                   of the rocket-forever bug pre-ORB-294. The retry just sends
                   a fresh chat message that the orchestrator re-enqueues; no
                   new endpoint needed. */
                _jsx("div", { className: "h-full flex items-center justify-center p-8", children: _jsxs("div", { className: "max-w-md text-center space-y-4", children: [_jsxs("div", { className: "text-sm font-medium text-slate-800 dark:text-slate-100", children: ["Webster couldn't finish this ", assetType.toLowerCase(), "."] }), assetDetail?.DraftGenerationError && (_jsx("div", { className: "text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap break-words", children: assetDetail.DraftGenerationError })), _jsx("div", { className: "text-xs text-slate-500 dark:text-slate-400", children: "Use the chat to describe what you want and Webster will try again." })] }) })) : rocketPhase !== 'ready' ? (_jsx(RocketLaunch, { phase: rocketPhase === 'blasting-off' ? 'blasting-off' : 'idle', onComplete: handleRocketComplete, blastOffDuration: 2000 })) : assetType === 'Email Template' ? (_jsxs("div", { className: "relative h-full", children: [_jsx(UnlayerDesigner, { ref: unlayerEditorRef, initialDesign: assetDetail?.DraftDesign ?? undefined, editable: !isGenerating, onUpdate: handleUnlayerUpdate, mergeTags: TEMPLATE_MERGE_TAGS, className: "h-full" }, templateDesignerKey), templateSaveStatus !== 'idle' && (_jsx("div", { className: "absolute top-2 right-2 z-10 px-2 py-1 rounded-md bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 shadow-sm pointer-events-auto", children: _jsx(SaveStatusIndicator, { status: templateSaveStatus, error: templateSaveError, onRetry: handleTemplateSaveRetry }) }))] })) : assetDetail ? (_jsx(EmailCopyEditorPanel, { ref: emailCopyPanelRef, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, webId: webId, asset: assetDetail, editable: !isGenerating, onSaved: async () => { await refreshAsset(); } })) : null }), _jsx("div", { className: "w-[420px] flex-shrink-0 flex flex-col border-l border-slate-200 dark:border-slate-700", children: _jsx(WebsterChatPanel, { chat: { messages, generating }, onSend: sendMessage, disabled: isGenerating, lockedByUserName: lockedByUserName, placeholder: placeholder, emptyMessage: _jsx("p", { className: "text-sm text-slate-400 dark:text-slate-500 text-center px-4", children: emptyMessage }) }) })] }));
}
